import { IResponse, IResponsePagination } from '@/types/common';
import {
  ICreateAyumiTopicRequest,
  IGetListAyumiRequest,
  IReorderAyumiCategoriesRequest,
  IReorderAyumiIntroVideosRequest,
  ISaveAyumiCategoryRequest,
  ISaveAyumiIntroVideoItemRequest,
  IUpdateAyumiIntroVideoRequest,
  IUpdateAyumiTopicKnowRequest,
  IUpdateAyumiTopicStatusRequest,
} from '@/types/request';
import {
  IAyumiCategoryResponse,
  IAyumiIntroVideo,
  IAyumiIntroVideoItem,
  IAyumiTopicKeyOption,
  IGetDetailAyumiResponse,
  IGetListAyumiResponse,
} from '@/types/response';
import axiosClient from '../axiosClient';
import { ENDPOINT } from '../endpoint';

const withId = (path: string, id: number) => path.replace(':id', String(id));

const withCategoryId = (path: string, id: number, categoryId: number) =>
  withId(path, id).replace(':categoryId', String(categoryId));

class Ayumi {
  getList = (
    params: IGetListAyumiRequest,
  ): Promise<IResponsePagination<IGetListAyumiResponse>> => {
    return axiosClient.get(ENDPOINT.AYUMI.GET_LIST, { params });
  };

  /** 新規作成のプルダウン用。7 課題を既定の並び順で返す */
  getTopicKeys = (): Promise<IResponse<IAyumiTopicKeyOption[]>> => {
    return axiosClient.get(ENDPOINT.AYUMI.GET_KEYS);
  };

  getDetail = (id: number): Promise<IResponse<IGetDetailAyumiResponse>> => {
    return axiosClient.get(withId(ENDPOINT.AYUMI.GET_DETAIL, id));
  };

  create = (
    body: ICreateAyumiTopicRequest,
  ): Promise<IResponse<IGetDetailAyumiResponse>> => {
    return axiosClient.post(ENDPOINT.AYUMI.CREATE, body);
  };

  /** 「課題を知る」タブの保存 */
  updateKnow = (
    body: IUpdateAyumiTopicKnowRequest,
    id: number,
  ): Promise<IResponse<IGetDetailAyumiResponse>> => {
    return axiosClient.put(withId(ENDPOINT.AYUMI.UPDATE_KNOW, id), body);
  };

  /** 公開する / 下書き保存 / 非表示にする */
  updateStatus = (
    body: IUpdateAyumiTopicStatusRequest,
    id: number,
  ): Promise<IResponse<IGetDetailAyumiResponse>> => {
    return axiosClient.patch(withId(ENDPOINT.AYUMI.UPDATE_STATUS, id), body);
  };

  delete = (id: number): Promise<IResponse<any>> => {
    return axiosClient.delete(withId(ENDPOINT.AYUMI.DELETE, id));
  };

  createCategory = (
    body: ISaveAyumiCategoryRequest,
    id: number,
  ): Promise<IResponse<IAyumiCategoryResponse>> => {
    return axiosClient.post(withId(ENDPOINT.AYUMI.CREATE_CATEGORY, id), body);
  };

  updateCategory = (
    body: ISaveAyumiCategoryRequest,
    id: number,
    categoryId: number,
  ): Promise<IResponse<IAyumiCategoryResponse>> => {
    return axiosClient.put(
      withCategoryId(ENDPOINT.AYUMI.UPDATE_CATEGORY, id, categoryId),
      body,
    );
  };

  deleteCategory = (
    id: number,
    categoryId: number,
  ): Promise<IResponse<any>> => {
    return axiosClient.delete(
      withCategoryId(ENDPOINT.AYUMI.DELETE_CATEGORY, id, categoryId),
    );
  };

  reorderCategories = (
    body: IReorderAyumiCategoriesRequest,
    id: number,
  ): Promise<IResponse<IGetDetailAyumiResponse>> => {
    return axiosClient.patch(
      withId(ENDPOINT.AYUMI.REORDER_CATEGORIES, id),
      body,
    );
  };

  /** 導入ボトムシートの紹介動画。課題に紐づかない 1 件だけの設定 */
  getIntroVideo = (): Promise<IResponse<IAyumiIntroVideo>> => {
    return axiosClient.get(ENDPOINT.AYUMI.GET_INTRO_VIDEO);
  };

  updateIntroVideo = (
    body: IUpdateAyumiIntroVideoRequest,
  ): Promise<IResponse<IAyumiIntroVideo>> => {
    return axiosClient.put(ENDPOINT.AYUMI.UPDATE_INTRO_VIDEO, body);
  };

  /** 課題一覧の紹介動画（複数本）。導入ボトムシートの紹介動画とは別物 */
  getIntroVideoItems = (): Promise<IResponse<IAyumiIntroVideoItem[]>> => {
    return axiosClient.get(ENDPOINT.AYUMI.GET_INTRO_VIDEOS);
  };

  createIntroVideoItem = (
    body: ISaveAyumiIntroVideoItemRequest,
  ): Promise<IResponse<IAyumiIntroVideoItem>> => {
    return axiosClient.post(ENDPOINT.AYUMI.CREATE_INTRO_VIDEO_ITEM, body);
  };

  updateIntroVideoItem = (
    body: ISaveAyumiIntroVideoItemRequest,
    id: number,
  ): Promise<IResponse<IAyumiIntroVideoItem>> => {
    return axiosClient.put(
      withId(ENDPOINT.AYUMI.UPDATE_INTRO_VIDEO_ITEM, id),
      body,
    );
  };

  deleteIntroVideoItem = (id: number): Promise<IResponse<any>> => {
    return axiosClient.delete(
      withId(ENDPOINT.AYUMI.DELETE_INTRO_VIDEO_ITEM, id),
    );
  };

  reorderIntroVideoItems = (
    body: IReorderAyumiIntroVideosRequest,
  ): Promise<IResponse<IAyumiIntroVideoItem[]>> => {
    return axiosClient.patch(ENDPOINT.AYUMI.REORDER_INTRO_VIDEOS, body);
  };
}

export const AyumiService = new Ayumi();
